import React, { useState, useEffect } from 'react';
import { 
  Box, Stack, Group, Title, Button, Card, Text, Badge, Modal, 
  TextInput, Textarea, Select, ActionIcon, Flex, ScrollArea 
} from '@mantine/core';
import { IconPlus, IconTrash, IconBriefcase, IconShoppingCart } from '@tabler/icons-react';

export default function AnnouncementsTab({ user, apiBaseUrl, t }) {
  const [myAnnouncements, setMyAnnouncements] = useState([]);
  
  const [modalOpened, setModalOpened] = useState(false);
  const [form, setForm] = useState({ type: 'JOB', title: '', description: '' });

  const [copyModalOpened, setCopyModalOpened] = useState(false);
  const [successModalOpened, setSuccessModalOpened] = useState(false);

  const fetchMine = async () => {
    if (!user?.email) return;
    try {
      const res = await fetch(`${apiBaseUrl}/api/announcements?ownerEmail=${user.email}`);
      if (res.ok) setMyAnnouncements(await res.json());
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchMine();
  }, [user]);

  const handleSave = async () => {
    if (!form.title) return;
    try {
      const res = await fetch(`${apiBaseUrl}/api/announcements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, ownerEmail: user.email, active: true })
      });
      if (res.ok) {
        setModalOpened(false);
        setForm({ type: 'JOB', title: '', description: '' });
        fetchMine();
        setSuccessModalOpened(true);
      }
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${apiBaseUrl}/api/announcements/${id}`, { method: 'DELETE' });
      fetchMine();
    } catch (e) { console.error(e); }
  };

  const renderCard = (a, isMine) => (
    <Card key={a.id} shadow="sm" radius="md" withBorder p="lg">
      <Group justify="space-between" mb="xs">
        <Group>
          {a.type === 'JOB' ? <IconBriefcase size={20} color="blue"/> : <IconShoppingCart size={20} color="orange"/>}
          <Title order={5}>{a.title}</Title>
          <Badge color={a.type === 'JOB' ? 'blue' : 'orange'}>
            {a.type === 'JOB' ? t('badge_job') : t('badge_purchase')}
          </Badge>
        </Group>
        {isMine && (
          <Group gap="xs">
            <ActionIcon color="indigo" variant="light" onClick={() => {
              const url = `${window.location.origin}/?job=${a.id}`;
              navigator.clipboard.writeText(url);
              setCopyModalOpened(true);
            }}>
              <IconBriefcase size={16}/>
            </ActionIcon>
            <ActionIcon color="red" variant="outline" onClick={() => handleDelete(a.id)}>
              <IconTrash size={16}/>
            </ActionIcon>
          </Group>
        )}
      </Group>
      <Text size="sm" c="dimmed" mb="md">{a.ownerEmail} • {new Date(a.createdAt).toLocaleDateString()}</Text>
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{a.description}</Text>
    </Card>
  );

  return (
    <Box p="md" h="100%">
      <Group justify="space-between" mb="lg">
        <Title order={3}>{t('b2b_announcements_board')}</Title>
        <Button leftSection={<IconPlus size={16}/>} onClick={() => setModalOpened(true)}>
          {t('add_announcement')}
        </Button>
      </Group>

        <ScrollArea h="calc(100vh - 150px)">
          <Stack gap="md">
            {myAnnouncements.map(a => renderCard(a, true))}
            {myAnnouncements.length === 0 && <Text c="dimmed">{t('no_announcements_mine')}</Text>}
          </Stack>
        </ScrollArea>

      <Modal opened={modalOpened} onClose={() => setModalOpened(false)} title={t('new_announcement')}>
        <Stack gap="md">
          <Select 
            label={t('announcement_type_label')}
            data={[
              { value: 'JOB', label: t('announcement_type_job') },
              { value: 'PURCHASE', label: t('announcement_type_purchase') }
            ]}
            value={form.type}
            onChange={(v) => setForm({ ...form, type: v })}
          />
          <TextInput 
            label={t('announcement_title_label')}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={t('announcement_title_placeholder')}
            required
          />
          <Textarea 
            label={t('announcement_desc_label')}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            minRows={5}
            placeholder={t('announcement_desc_placeholder')}
          />
          <Button onClick={handleSave} fullWidth mt="md">{t('announcement_save')}</Button>
        </Stack>
      </Modal>

      <Modal opened={successModalOpened} onClose={() => setSuccessModalOpened(false)} withCloseButton={false} centered styles={{ content: { backgroundColor: '#1a1a1a', color: 'white' } }}>
        <Stack align="center" p="md">
          <IconBriefcase size={50} color="#4caf50" />
          <Title order={4} style={{ color: 'white' }}>Ogłoszenie zostało dodane!</Title>
          <Text ta="center" style={{ color: '#ccc' }}>
            Na Twój adres e-mail wysłaliśmy link potwierdzający. 
            Ogłoszenie będzie widoczne po kliknięciu w ten link.
          </Text>
          <Button color="green" onClick={() => setSuccessModalOpened(false)} mt="md">Rozumiem</Button>
        </Stack>
      </Modal>

      <Modal opened={copyModalOpened} onClose={() => setCopyModalOpened(false)} withCloseButton={false} centered styles={{ content: { backgroundColor: '#1a1a1a', color: 'white' } }}>
        <Stack align="center" p="md">
          <Title order={4} style={{ color: 'white' }}>Link skopiowany!</Title>
          <Text ta="center" style={{ color: '#ccc' }}>
            Link do ogłoszenia został skopiowany do schowka.
          </Text>
          <Button color="indigo" onClick={() => setCopyModalOpened(false)} mt="md">OK</Button>
        </Stack>
      </Modal>
    </Box>
  );
}
